/*
 * Deliver-ME public landing page - configuration.
 *
 * Everything in this file is a knob the business may want to turn without a
 * developer hunting through components. Copy lives in ./content.js instead.
 */

import { getCurrentSubdomain } from '../../commons/domain';
import { API_HOST, HTTP_PROTOCOL } from '../../config';

/* ------------------------------------------------------------------ *
 * 1. Savings calculator
 * ------------------------------------------------------------------ */

/*
 * Savings rate used by the calculator. Set by Dipendra (Dev Lead).
 *
 * 18% is the figure from the original design and is what we are shipping.
 * This is the ONE place it lives - do not inline it anywhere else.
 *
 * If it changes, also update the disclaimer sentence in ./content.js
 * (SAVINGS.note), which spells "18%" out in words.
 */
export const SAVINGS_RATE = 0.18;

/* Slider bounds and defaults - change here, nowhere else. */
export const SAVINGS_CALCULATOR = {
    shipments: { min: 20, max: 3000, step: 10, initial: 300 },
    costPerShipment: { min: 8, max: 80, step: 1, initial: 22 },
};

/* ------------------------------------------------------------------ *
 * 2. Primary call to action - "Book a Free Demo"
 * ------------------------------------------------------------------ */

/*
 * Destination for every "Book a Free Demo" control on the page. Set by
 * Dipendra (Dev Lead).
 *
 * Swap this ONE line to change where they all go:
 *
 *   Contact form  -> '#contact'                  (scrolls to the form, topic
 *                                                 pre-set to DEMO_TOPIC)
 *   Booking link  -> 'https://calendly.com/...'  (set DEMO_IS_EXTERNAL true)
 *   In-app route  -> '/book-a-demo'              (set DEMO_IS_EXTERNAL false)
 *   Old mailto    -> 'mailto:info@deliver-me.com.au?subject=Free%20demo%20request'
 *
 * Nothing else needs to change - every CTA reads from here.
 */
export const DEMO_URL = '#contact';

/* Ignored when DEMO_URL starts with '#'.
 * true  -> rendered as <a target="_blank" rel="noopener noreferrer">
 * false -> rendered as a react-router <Link>. */
export const DEMO_IS_EXTERNAL = true;

/* ------------------------------------------------------------------ *
 * 2b. Contact Us form
 * ------------------------------------------------------------------ */

/*
 * Server route that emails the enquiry to CONTACT_EMAIL. Unauthenticated POST,
 * JSON body - see services/contactService.js for the exact payload. The
 * server owns the recipient, Reply-To, subject, rate limiting and the honeypot
 * check; nothing the browser sends can change where the email goes.
 */
export const CONTACT_ENDPOINT = `${HTTP_PROTOCOL}://${API_HOST}/contact/`;

/* Shown to the visitor as a fallback if sending fails. */
export const CONTACT_EMAIL = 'info@deliver-me.com.au';

/* `value`s from CONTACT_TOPICS in ./content.js. */
export const CONTACT_DEFAULT_TOPIC = 'callback';
export const DEMO_TOPIC = 'demo';
export const CONTACT_PHONE_REQUIRED_TOPICS = ['callback'];

/* ------------------------------------------------------------------ *
 * 3. Links that already exist in the app
 * ------------------------------------------------------------------ */

/* The existing DME login screen. Unchanged - no new auth (DEV_BRIEF 1). */
export const LOGIN_ROUTE = '/login';

export const PHONE_DISPLAY = '(02) 8311 1500';
export const PHONE_HREF = 'tel:+61283111500';

/* ------------------------------------------------------------------ *
 * 4. Analytics hook (optional)
 * ------------------------------------------------------------------ */

/*
 * DEV_BRIEF section 4 asks for an event on "Book a Free Demo" and on "Login".
 * Wire whatever the app already uses in here; it is called with
 * ('demo' | 'login' | 'phone' | 'contact', { placement: '<section name>' })
 * and ('contact_submit', { topic }) after a successful enquiry.
 * Left as a no-op so the page ships without a new dependency.
 */
export const trackLandingEvent = (name, meta) => {
    if (typeof window !== 'undefined' && window.dataLayer && window.dataLayer.push)
        window.dataLayer.push(Object.assign({ event: 'landing_' + name }, meta));
};

/* ------------------------------------------------------------------ *
 * 5. Brand / route scoping - Deliver-ME ONLY
 * ------------------------------------------------------------------ */

/*
 * DEV_BRIEF section 3.3 and hard requirement 1: this page is Deliver-ME only.
 * GW and DSS keep the existing HomePage. This mirrors the existing detection in
 * src/pages/HomePage.js (lines ~137-142) so there is one rule, used in two
 * places: HomePage (to pick the component) and AppRouter (to hide app chrome).
 *
 * `clientname` is optional because AppRouter is not connected to redux. It is
 * only meaningful for a logged-in user, and HomePage already redirects a
 * logged-in user off '/' to '/home', so omitting it is safe.
 */
export const isGWBrand = (clientname) => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    return getCurrentSubdomain().indexOf('gw') !== -1
        || (!!isLoggedIn && clientname === 'Gebrüder Weiss')
        || localStorage.getItem('theme') === 'gw-theme';
};

export const isDSSBrand = (clientname) => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    return getCurrentSubdomain().indexOf('dss') !== -1
        || (!!isLoggedIn && clientname === 'DSS')
        || localStorage.getItem('theme') === 'dss-theme';
};

/* The public marketing entry point. Only '/'. '/home' stays the app home. */
export const LANDING_PATH = '/';

/*
 * localStorage, not redux: state.auth.isLoggedIn is still false on a fresh page
 * load until getUser() resolves, so it cannot be trusted at mount time.
 * Note the explicit 'true' - the app stores the string 'false' on logout, which
 * is itself truthy.
 */
export const isLoggedInUser = () => localStorage.getItem('isLoggedIn') === 'true';

/*
 * Single source of truth: should the visitor at `pathname` see the new landing
 * page instead of the existing HomePage?
 *
 * Logged-in Deliver-ME users may browse '/' (e.g. via the header logo); Login
 * on the landing page resumes an existing session on /login. GW/DSS unchanged.
 */
export const shouldRenderLandingPage = (pathname, clientname) =>
    pathname === LANDING_PATH
    && !isGWBrand(clientname)
    && !isDSSBrand(clientname);

/*
 * The landing page brings its own sticky header and its own footer, so the
 * app-wide <Header /> / <Footer /> must not render underneath it.
 */
export const shouldHideAppChrome = (pathname, clientname) =>
    shouldRenderLandingPage(pathname, clientname);



// WEBPACK FOOTER //
// src/pages/landing/landingConfig.js